import { IQuoteOfferingRespSchema } from '@monetize/types/app';
import {
  getQuoteOfferingsWithChildren,
  isQuoteOfferingAddedToAmendment,
} from '../../../../utils/quotes';

export interface QuoteOfferingSkeletonBlock {
  /** Top-level offering id */
  id: string;
  /** The expanded header is taller when it reserves a line for the description */
  showsDescription: boolean;
  /** Offerings with scheduled changes render an extra expand/collapse row */
  hasScheduledChanges: boolean;
  /**
   * Whether this offering was added on the amendment. The amendment layout
   * renders those under their own "New Offerings" header, below the ones
   * carried over from the contract.
   */
  isAddedToAmendment: boolean;
  /**
   * Product count per segment, in render order: the offering itself followed
   * by its scheduled changes. Every segment renders a rate row above its
   * product rows.
   */
  itemCountsPerSegment: number[];
}

/**
 * Shape of the offerings table for the loading skeleton. Offerings mount
 * expanded, so the skeleton reserves roughly the expanded height rather than a
 * collapsed strip; the swap to the real table then shifts the page very little.
 *
 * Groups through the same helper the table itself uses, so the blocks land in
 * the same order as the rows they stand in for.
 */
export const planQuoteOfferingsSkeleton = (
  quoteOfferings: IQuoteOfferingRespSchema[],
  {
    canEditDescriptions = false,
  }: {
    /**
     * Whether the quote is editable. An editable offering renders its
     * description field even when the description is empty, so the header
     * reserves that line either way.
     */
    canEditDescriptions?: boolean;
  } = {},
): QuoteOfferingSkeletonBlock[] =>
  getQuoteOfferingsWithChildren(quoteOfferings)
    // A segment whose parent is missing from the quote has no block of its own
    .filter(({ quoteOffering }) => !!quoteOffering)
    .map(({ quoteOffering, children }) => ({
      id: quoteOffering.id,
      showsDescription:
        !!quoteOffering.description ||
        (canEditDescriptions && !quoteOffering.locked),
      hasScheduledChanges: children.length > 0,
      isAddedToAmendment: !!isQuoteOfferingAddedToAmendment(quoteOffering),
      itemCountsPerSegment: [quoteOffering, ...children].map(
        ({ items }) => items?.length ?? 0,
      ),
    }));
